import * as yup from 'yup';
import { PreAppData, PreAppFormValues } from './types';
import { PreAppRequestTypes } from '@src/utils/enums';

export const preAppSchema = yup.object().shape({
  preAppRequestType: yup
    .string()
    .test(
      'isDefault',
      'DEFAULT_VALUE is a reserved word, please enter a valid string',
      (val?: string) => !val || val.toUpperCase() !== 'DEFAULT_VALUE'
    )
    .required('Please select an option'),
  scopingCommentsDueDate: yup
    .string()
    .when('preAppRequestType', {
      is: PreAppRequestTypes.ScopingResponse,
      then: () => yup.string().required('Please select a date'),
      otherwise: () => yup.string().nullable(),
    })
    .nullable(),
  isOtherLeadFederalAgency: yup.string().required('Please select an option'),
  isOtherApplicableLaw: yup.string().required('Please select an option').typeError('Please select an option'),
  applicableLaws: yup.array().when('isOtherApplicableLaw', {
    is: 'Yes',
    then: () =>
      yup
        .array()
        .min(1, 'Please select at least one applicable law')
        .typeError('Please select at least one applicable law'),
    otherwise: () => yup.array().nullable(),
  }),
  speciesDetails: yup
    .string()
    .when(['isOtherApplicableLaw', 'applicableLaws'], {
      is: (isOtherApplicableLaw?: string, applicableLaws?: string[]) =>
        isOtherApplicableLaw === 'Yes' && (applicableLaws ?? []).includes('isSpeciesPresent'),
      then: () => yup.string().required('Field is required'),
      otherwise: () => yup.string().nullable(),
    })
    .nullable(),
  govResourceConcernDetails: yup
    .string()
    .when(['isOtherApplicableLaw', 'applicableLaws'], {
      is: (isOtherApplicableLaw?: string, applicableLaws?: string[]) =>
        isOtherApplicableLaw === 'Yes' && (applicableLaws ?? []).includes('isGovResourceConcern'),
      then: () => yup.string().required('Field is required'),
      otherwise: () => yup.string().nullable(),
    })
    .nullable(),
  historicalResourceConcernDetails: yup
    .string()
    .when(['isOtherApplicableLaw', 'applicableLaws'], {
      is: (isOtherApplicableLaw?: string, applicableLaws?: string[]) =>
        isOtherApplicableLaw === 'Yes' && (applicableLaws ?? []).includes('isHistoricalResourceConcern'),
      then: () => yup.string().required('Field is required'),
      otherwise: () => yup.string().nullable(),
    })
    .nullable(),
  section408_Details: yup
    .string()
    .when(['isOtherApplicableLaw', 'applicableLaws'], {
      is: (isOtherApplicableLaw?: string, applicableLaws?: string[]) =>
        isOtherApplicableLaw === 'Yes' && (applicableLaws ?? []).includes('isSection408_Required'),
      then: () => yup.string().required('Field is required'),
      otherwise: () => yup.string().nullable(),
    })
    .nullable(),
});

export const getApplicableLawsFromBools = (preAppData?: PreAppData) =>
  [
    preAppData?.isSpeciesPresent ? 'isSpeciesPresent' : null,
    preAppData?.isGovResourceConcern ? 'isGovResourceConcern' : null,
    preAppData?.isHistoricalResourceConcern ? 'isHistoricalResourceConcern' : null,
    preAppData?.isSection408_Required ? 'isSection408_Required' : null,
  ].filter(Boolean);

export const getPreAppDefaultValues = ({ preAppData }: { preAppData?: PreAppData }) => {
  const shouldShowApplicableLawValues = preAppData?.isOtherApplicableLaw === 'Yes';
  const shouldShowScopingCommentsDueDate = preAppData?.preAppRequestType === PreAppRequestTypes.ScopingResponse;

  const applicableLaws = shouldShowApplicableLawValues ? getApplicableLawsFromBools(preAppData) : [];

  const hasSpeciesPresent = applicableLaws.includes('isSpeciesPresent');
  const hasGovResourceConcern = applicableLaws.includes('isGovResourceConcern');
  const hasHistoricalResourceConcern = applicableLaws.includes('isHistoricalResourceConcern');
  const hasSection408Required = applicableLaws.includes('isSection408_Required');

  return {
    preAppRequestType: preAppData?.preAppRequestType ?? null,
    scopingCommentsDueDate: shouldShowScopingCommentsDueDate
      ? (preAppData?.scopingCommentsDueDate?.slice(0, 10) ?? null)
      : null,
    isOtherLeadFederalAgency: preAppData?.isOtherLeadFederalAgency ?? null,
    isOtherApplicableLaw: preAppData?.isOtherApplicableLaw ?? null,
    applicableLaws,

    speciesDetails: hasSpeciesPresent ? (preAppData?.speciesDetails ?? '') : '',
    govResourceConcernDetails: hasGovResourceConcern ? (preAppData?.govResourceConcernDetails ?? '') : '',
    historicalResourceConcernDetails: hasHistoricalResourceConcern
      ? (preAppData?.historicalResourceConcernDetails ?? '')
      : '',
    section408_Details: hasSection408Required ? (preAppData?.section408_Details ?? '') : '',
  };
};

export const getPreAppPayload = (values: PreAppFormValues) => {
  const shouldKeepApplicableLawValues = values?.isOtherApplicableLaw === 'Yes';
  const selectedApplicableLaws = shouldKeepApplicableLawValues ? (values?.applicableLaws ?? []) : [];
  const shouldKeepScopingCommentsDueDate = values?.preAppRequestType === PreAppRequestTypes.ScopingResponse;

  const hasSpeciesPresent = selectedApplicableLaws.includes('isSpeciesPresent');
  const hasGovResourceConcern = selectedApplicableLaws.includes('isGovResourceConcern');
  const hasHistoricalResourceConcern = selectedApplicableLaws.includes('isHistoricalResourceConcern');
  const hasSection408Required = selectedApplicableLaws.includes('isSection408_Required');

  return {
    preAppRequestType: values?.preAppRequestType,
    scopingCommentsDueDate: shouldKeepScopingCommentsDueDate ? values?.scopingCommentsDueDate : null,
    isOtherLeadFederalAgency: values?.isOtherLeadFederalAgency,
    isOtherApplicableLaw: values?.isOtherApplicableLaw,

    speciesDetails: shouldKeepApplicableLawValues && hasSpeciesPresent ? values?.speciesDetails : null,
    govResourceConcernDetails:
      shouldKeepApplicableLawValues && hasGovResourceConcern ? values?.govResourceConcernDetails : null,
    historicalResourceConcernDetails:
      shouldKeepApplicableLawValues && hasHistoricalResourceConcern ? values?.historicalResourceConcernDetails : null,
    section408_Details: shouldKeepApplicableLawValues && hasSection408Required ? values?.section408_Details : null,

    isSpeciesPresent: shouldKeepApplicableLawValues ? hasSpeciesPresent : null,
    isGovResourceConcern: shouldKeepApplicableLawValues ? hasGovResourceConcern : null,
    isHistoricalResourceConcern: shouldKeepApplicableLawValues ? hasHistoricalResourceConcern : null,
    isSection408_Required: shouldKeepApplicableLawValues ? hasSection408Required : null,

    hasAddPropertyOwners: values?.hasAddPropertyOwners,
    hasSupportingDocuments: values?.hasSupportingDocuments,
  };
};
